/**
 * Document content-read and structured-field extraction status, rendered
 * separately next to the filename. Indexed content never implies complete
 * ingestion or that all source facts have been mapped to SED field slots.
 *
 * Surfaces the `SiteDocument.extractionStatus` UPE-1 field as a small
 * coloured pill with hover-tooltip copy keyed off the status. Pre-UPE
 * docs (no `extractionStatus` field) render as `'pending'` until the
 * next SED rebuild stamps a real status onto the doc.
 *
 * Failure variants (`failed-quota`, `failed-parse`, `failed-timeout`,
 * `failed-unknown`) and `success-partial` are clickable — the click
 * fires `onRetry(docId)` which composes with the DVF-3 re-extract
 * pattern (`reExtractDocForSed`). The chip stays purely presentational:
 * the host owns the retry plumbing so this component is unit-testable
 * without a store.
 *
 * DLS-1 — `'pending'` is ALSO clickable now (an escape-hatch "Scan
 * now"). Doc extraction is section-driven: `applyDocAwareConfidence`
 * only extracts the single best-matching doc per UNAVAILABLE section,
 * so a doc that no report section currently needs is left at
 * `'pending'` permanently — the next rebuild leaves it pending too.
 * The "Scan now" affordance routes that doc through the SAME single-doc
 * extraction path in FORCE mode (`reExtractDocForSed(..., { force })`)
 * so the user can always force-extract a stranded doc. The chip surfaces
 * the action via `onRetry` (one host handler, shared with Retry) but the
 * label + tooltip distinguish "Scan now" (pending) from "Retry" (failed
 * / partial). The tooltip copy is also made honest: a pending doc that
 * has been through a rebuild reads "no report section needs this
 * document — use Scan now to force extraction", not the misleading
 * "will be scanned on next SED rebuild".
 *
 * Palette mirrors the platform's existing semantic tokens — green
 * for success, amber for partial / pending, red for failure, neutral
 * for pending / extracting.
 */
import React from 'react';
import { ScanLine, RotateCw } from 'lucide-react';
import type { SiteDocument, ExtractionStatus } from '../../../types/siteRag';
import type { StatusPillVariant } from '../../ui/aurora';
import { documentMappingSummary } from '../../../lib/documentMappingOutcome';
import { documentExtractionProviderBlocked } from '../../../services/knowledge/documentExtractionFailure';

interface Props {
  doc: SiteDocument;
  /**
   * Fired when a clickable chip is clicked. Covers BOTH the retry path
   * (`failed-*` / `success-partial`) AND the DLS-1 "Scan now" path
   * (`pending`). The host routes both through `handleReExtractDoc` →
   * `reExtractDocForSed`; the `force` flag is decided host-side for the
   * pending case (see DocumentLibrary.handleReExtractDoc).
   */
  onRetry?: (docId: string) => void;
  /** Override the rendered status (used by tests). */
  statusOverride?: ExtractionStatus;
}

/**
 * Map each extraction status onto an Aurora-Cockpit StatusPill variant
 * (`.da-status--*`; see src/styles/auroraCockpit.css). Semantics preserved:
 * green for success, info for in-flight scan, amber for partial, neutral for
 * pending, danger for every failure variant.
 */
const STATUS_VARIANT: Record<ExtractionStatus, StatusPillVariant> = {
  'pending':         'neutral',
  'extracting':      'info',
  'success-full':    'go',
  'success-partial': 'cond',
  'failed-quota':    'nogo',
  'failed-parse':    'nogo',
  'failed-timeout':  'nogo',
  'failed-unknown':  'nogo',
  'failed-no-text':  'nogo',
};

const VARIANT_MODIFIER: Record<StatusPillVariant, string> = {
  go: 'da-status--go',
  cond: 'da-status--cond',
  'cond-high': 'da-status--condh',
  nogo: 'da-status--nogo',
  info: 'da-status--info',
  neutral: 'da-status--neutral',
};

const STATUS_LABEL: Record<ExtractionStatus, string> = {
  'pending':         'PENDING',
  'extracting':      'SCANNING',
  'success-full':    'SCANNED',
  'success-partial': 'PARTIAL',
  'failed-quota':    'QUOTA',
  'failed-parse':    'PARSE',
  'failed-timeout':  'TIMEOUT',
  'failed-unknown':  'FAILED',
  'failed-no-text':  'NO TEXT',
};

/** Content indexing and structured-field mapping are separate outcomes. */
function contentReadState(doc: SiteDocument): { label: string; tooltip: string; partial: boolean } | null {
  const positiveCount = (value: number | undefined) => typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : 0;
  const topicCount = positiveCount(doc.topics?.segmentCount)
    || (doc.evidenceDigest?.text?.trim() ? positiveCount(doc.evidenceDigest.topicCount) : 0);
  const visualCount = Math.max(positiveCount(doc.visualReadingCount), doc.visualReadings?.length ?? 0);
  if (!topicCount && !visualCount) return null;
  const qualifications = [...(doc.evidenceDigest?.warnings ?? []).filter(warning => warning.trim())];
  let partial = false;
  if (doc.truncated) { qualifications.push('Extracted text was truncated; retained content is incomplete.'); partial = true; }
  if (doc.topics?.coverage?.windows.some(window => window.status === 'failed')) {
    qualifications.push('Some document content windows could not be indexed.'); partial = true;
  }
  if (doc.topics?.coverage?.segmentsTruncated) { qualifications.push('The topic index reached its segment limit.'); partial = true; }
  if (doc.visualCoverage?.unreadPages.length) {
    qualifications.push(`Visual evidence remains unread on pages ${doc.visualCoverage.unreadPages.join(', ')}.`); partial = true;
  }
  if (doc.visualReadings?.some(reading => reading.transcriptionIncomplete)) {
    qualifications.push('Some scanned-page text remains unreadable or incomplete.'); partial = true;
  }
  if (doc.pdfCoverage && doc.pdfCoverage.textPagesRead < doc.pdfCoverage.totalPages) {
    qualifications.push(`Text extracted from ${doc.pdfCoverage.textPagesRead} of ${doc.pdfCoverage.totalPages} PDF pages; visual-read coverage is separate.`); partial = true;
  }
  if (doc.enrichmentStatus === 'deferred' || (doc.enrichmentDeferredStages?.length ?? 0) > 0) {
    const stages = doc.enrichmentDeferredStages?.length ? doc.enrichmentDeferredStages.join(', ') : 'summarization/indexing';
    qualifications.push(`Optional enrichment (${stages}) was deferred due to timeout; verified original bytes are preserved.`);
    partial = true;
  }
  const observations = [topicCount ? `${topicCount} indexed topic${topicCount === 1 ? '' : 's'}` : '', visualCount ? `${visualCount} visual reading${visualCount === 1 ? '' : 's'}` : ''].filter(Boolean).join('; ');
  const explanation = `Document content read: ${observations}. Indexed document facts and observations can inform reports separately from structured SED field slots. This does not establish complete document coverage or that every fact is mapped to a field.`;
  return { label: topicCount ? (partial ? 'CONTENT PARTIAL' : 'CONTENT READ') : 'IMAGE READ',
    tooltip: [explanation, ...(visualCount ? ['Visual observations are screening evidence; they do not establish service availability or approval.'] : []), ...new Set(qualifications)].join(' '),
    partial: partial || qualifications.length > 0 };
}

function fieldMappingTooltip(doc: SiteDocument): string {
  const count = doc.populatedSlotCount ?? 0;
  const slots = `${count} structured SED field slot${count === 1 ? '' : 's'}`;
  const error = doc.extractionStatusError ? ` ${doc.extractionStatusError}` : '';
  switch (doc.extractionStatus ?? 'pending') {
    case 'pending': return `Structured-field mapping is pending; indexed document content remains available. Use Scan now to map fields.`;
    case 'extracting': return 'Structured-field mapping is running; indexed content and field slots are separate.';
    case 'success-full': return `Structured-field mapping completed with ${slots} populated. This count does not measure all indexed document facts or full document coverage.`;
    case 'success-partial': return `Structured-field mapping is partial (${slots} populated); some target fields remain unfilled. Indexed document content remains available. Click to retry field mapping.`;
    case 'failed-no-text': return 'Structured-field mapping found no machine-readable text. Retained indexed or visual content is a separate result; OCR or a text-based copy may still be needed for field mapping.';
    case 'failed-quota': return `Structured-field mapping failed because the extraction quota was exceeded.${error} Indexed document content remains available. Click to retry field mapping.`;
    case 'failed-parse': return `Structured-field mapping failed because its response could not be parsed.${error} Indexed document content remains available. Click to retry field mapping.`;
    case 'failed-timeout': return `Structured-field mapping timed out.${error} Indexed document content remains available. Click to retry field mapping.`;
    case 'failed-unknown': return `Structured-field mapping failed.${error} Indexed document content remains available. Click to retry field mapping.`;
  }
}

/** New process receipts clarify legacy PARTIAL without changing the source's evidence grade. */
function mappingReceiptState(doc: SiteDocument): { label: string; tooltip: string; actionable: boolean; variant: StatusPillVariant } | null {
  const receipt = doc.extractionOutcome;
  if (!receipt || receipt.version !== 1 || !Array.isArray(receipt.targets)
    || !Number.isSafeInteger(receipt.retainedFieldCount) || receipt.retainedFieldCount < 0
    || !Number.isSafeInteger(receipt.linkedSectionCount) || receipt.linkedSectionCount < 0
    || doc.extractionStatus === 'extracting' || doc.extractionStatus === 'failed-no-text') return null;
  const summary = documentMappingSummary(receipt);
  const counts = `${receipt.retainedFieldCount} retained source-bound field${receipt.retainedFieldCount === 1 ? '' : 's'}; ${receipt.linkedSectionCount} linked section${receipt.linkedSectionCount === 1 ? '' : 's'}. These counts do not establish complete document coverage or requirement fulfillment.`;
  const details = [summary.deferred ? `${summary.deferred} target(s) deferred.` : '', summary.failed ? `${summary.failed} target(s) failed after bounded attempts.` : '',
    summary.empty ? `${summary.empty} completed target(s) returned no eligible fields.` : '', summary.rejected ? `${summary.rejected} target(s) were not adopted by source scope, validation, field policy, or prior review checks.` : '',
    summary.unsupported ? `${summary.unsupported} target(s) have no applicable schema or readable source; retrying cannot add a missing schema.` : ''].filter(Boolean).join(' ');
  const tooltip = `${counts} ${details}`.trim();
  const pending = receipt.targets.filter(target => target.status === 'failed' || target.status === 'deferred');
  if (pending.length && pending.every(target => documentExtractionProviderBlocked(target.reason))) {
    return { label: 'FIELDS WAITING', actionable: false, variant: 'cond',
      tooltip: `${tooltip} Mapping is paused because the extraction service refused access. Existing source-backed fields are retained. A workspace administrator must restore service access before mapping can resume; re-uploading this document does not resolve the refusal.` };
  }
  if (summary.failed) return { label: 'FIELDS ERROR', tooltip: `${tooltip} Retry the failed mapping targets.`, actionable: true, variant: 'nogo' };
  if (summary.deferred) return { label: 'FIELDS QUEUED', tooltip: `${tooltip} Continue bounded field mapping.`, actionable: true, variant: 'cond' };
  if (!receipt.retainedFieldCount && !receipt.linkedSectionCount) {
    if (summary.rejected) return { label: 'FIELDS NOT ADOPTED', tooltip, actionable: false, variant: 'cond' };
    if (summary.empty) return { label: 'NO FIELD YIELD', tooltip, actionable: false, variant: 'neutral' };
    if (summary.unsupported) return { label: 'NO FIELD SCHEMA', tooltip, actionable: false, variant: 'neutral' };
    if (receipt.targets.length) return { label: 'NO RETAINED FIELDS', tooltip: `${tooltip} Mapping was assessed, but no source-bound field remains in the current evidence.`, actionable: false, variant: 'neutral' };
    return { label: 'FIELDS UNASSESSED', tooltip: `${tooltip} No current mapping targets were assessed. Use Scan now to evaluate mapping; a scan cannot invent missing source data.`, actionable: true, variant: 'neutral' };
  }
  return { label: 'FIELDS RETAINED', tooltip, actionable: false, variant: 'info' };
}

export function statusTooltip(doc: SiteDocument): string {
  const receipt = mappingReceiptState(doc);
  if (receipt) return receipt.tooltip;
  if (contentReadState(doc)) return fieldMappingTooltip(doc);
  const status: ExtractionStatus = doc.extractionStatus ?? 'pending';
  const slotCount = doc.populatedSlotCount ?? 0;
  const slotsCopy = slotCount === 1 ? '1 SED slot' : `${slotCount} SED slots`;
  const error = doc.extractionStatusError;
  switch (status) {
    case 'pending':
      // DLS-1 — honest copy. A doc that has already been through a SED
      // rebuild (extractionStatusAt stamped) but stayed 'pending' was
      // never section-matched: no report section needed it, so the
      // section-driven extractor skipped it — and the next rebuild will
      // skip it again. Tell the user that + point them at "Scan now"
      // (force extraction). A doc with no `extractionStatusAt` has
      // genuinely not been processed yet, so keep the original copy but
      // still offer the manual escape hatch.
      // Doc-extraction program — copy updated: with derived schemas +
      // tag-matching, a scan can extract requirement evidence fields even
      // when no report SECTION needs the doc, so "no report section needs
      // this document" no longer implies "nothing to extract".
      return typeof doc.extractionStatusAt === 'number'
        ? 'Not scanned into evidence yet. Use Scan now to extract sections and requirement fields.'
        : 'Not yet scanned — will be scanned on the next SED rebuild, or use Scan now to force it.';
    case 'extracting':
      return 'Scanning this document for SED evidence…';
    case 'success-full':
      return `Scanned — populated ${slotsCopy}.`;
    case 'success-partial':
      return `Scanned — partial extraction (${slotsCopy} populated); some sections or requirements yielded nothing. Click to re-scan.`;
    case 'failed-quota':
      return `Extraction failed — Gemini quota exceeded${error ? ` (${error})` : ''}. Click to retry.`;
    case 'failed-parse':
      return `Extraction failed — couldn't parse response${error ? ` (${error})` : ''}. Click to retry.`;
    case 'failed-timeout':
      return `Extraction timed out${error ? ` (${error})` : ''}. Click to retry.`;
    case 'failed-unknown':
      return `Extraction failed${error ? ` — ${error}` : ''}. Click to retry.`;
    case 'failed-no-text':
      // SPO-1 — deliberately NOT retryable: a re-scan would re-read the
      // same placeholder. The fix is upstream (re-upload a text-based
      // copy, or an OCR backfill re-extracts from the raw file).
      return 'No machine-readable text — scanned/image-based document; OCR produced no text. Re-upload a text-based copy (or run the OCR backfill) to make it scannable.';
  }
}

export function isRetryableStatus(status: ExtractionStatus): boolean {
  return (
    status === 'success-partial' ||
    status === 'failed-quota' ||
    status === 'failed-parse' ||
    status === 'failed-timeout' ||
    status === 'failed-unknown'
  );
}

/**
 * DLS-1 — `'pending'` is the only "scannable" status: the doc has not
 * yet been extracted into any SED section (either never processed, or
 * processed-but-never-section-matched). Surfacing the manual "Scan now"
 * action on pending is the escape hatch out of the dead-end where a
 * non-matching doc would otherwise stay pending across every rebuild.
 * `'extracting'` is in-flight (no action). All the retryable variants
 * are handled by `isRetryableStatus` instead.
 */
export function isScannableStatus(status: ExtractionStatus): boolean {
  return status === 'pending';
}

/**
 * DLS-1 — whether the chip exposes a click action at all. Pending →
 * "Scan now" (force extraction); failed-* / success-partial → "Retry".
 */
export function isActionableStatus(status: ExtractionStatus): boolean {
  return isRetryableStatus(status) || isScannableStatus(status);
}

export const DocRowStatusChip: React.FC<Props> = ({ doc, onRetry, statusOverride }) => {
  const status: ExtractionStatus = statusOverride ?? doc.extractionStatus ?? 'pending';
  const content = contentReadState(doc);
  const receipt = mappingReceiptState({ ...doc, extractionStatus: status });
  const tooltip = statusTooltip({ ...doc, extractionStatus: status });
  const scannable = isScannableStatus(status);
  const actionable = (receipt ? receipt.actionable : isActionableStatus(status)) && typeof onRetry === 'function';
  // DLS-1 — the pending chip shows "SCAN NOW" as its action affordance,
  // distinct from the static "PENDING" status label and from the
  // failed/partial "Retry" semantics. The status label itself stays
  // PENDING (the meaning is honest via the tooltip + the action).
  const label = receipt?.label ?? (content
    ? status === 'pending' ? 'FIELDS PENDING' : status === 'extracting' ? 'FIELDS MAPPING'
      : status === 'success-full' ? 'FIELDS MAPPED' : status === 'success-partial' ? 'FIELDS PARTIAL' : 'FIELDS FAILED'
    : scannable && actionable ? 'SCAN NOW' : STATUS_LABEL[status]);
  const actionVerb = receipt && doc.extractionOutcome && documentMappingSummary(doc.extractionOutcome).deferred > 0 ? 'Continue field mapping' : scannable ? 'Scan now' : 'Retry extraction';
  const handleClick = (e: React.MouseEvent) => {
    if (!actionable) return;
    e.stopPropagation();
    onRetry!(doc.id);
  };
  // Aurora-Cockpit StatusPill markup (`.da-status` + glow dot). When actionable
  // the chip becomes a button and gains a trailing Scan-now / Retry icon; the
  // visible status word stays the canonical STATUS_LABEL, except pending which
  // surfaces "SCAN NOW" as its action affordance (DLS-1).
  const variant = receipt?.variant ?? STATUS_VARIANT[status];
  const ActionIcon = scannable ? ScanLine : RotateCw;
  const commonProps = {
    'data-testid': `doc-row-${doc.id}-extraction-status`,
    'data-status': status,
    'data-action': actionable ? (scannable ? 'scan' : 'retry') : undefined,
    'aria-label': actionable
      ? `Extraction status: ${label}. ${actionVerb}. ${tooltip}`
      : `Extraction status: ${label}. ${tooltip}`,
    title: tooltip,
    className: `da-status ${VARIANT_MODIFIER[variant]} shrink-0${
      actionable ? ' cursor-pointer hover:opacity-80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary' : ''
    }`,
  } as const;
  const fieldChip = actionable ? (
    <button type="button" onClick={handleClick} {...commonProps}>
      <span className="da-status__dot" aria-hidden />
      {label}
      <ActionIcon className="w-2.5 h-2.5 ml-0.5" aria-hidden />
    </button>
  ) : (
    <span {...commonProps}>
      <span className="da-status__dot" aria-hidden />
      {label}
    </span>
  );
  const hasDeferredEnrichment = doc.enrichmentStatus === 'deferred' || (doc.enrichmentDeferredStages?.length ?? 0) > 0;
  const deferredStagesLabel = doc.enrichmentDeferredStages?.length ? doc.enrichmentDeferredStages.join(', ') : 'summarization/indexing';
  const deferredTooltip = `Optional enrichment (${deferredStagesLabel}) was deferred due to timeout; verified original bytes are preserved and can be re-indexed.`;
  if (!content && !hasDeferredEnrichment) return fieldChip;
  return <span className="inline-flex items-center flex-wrap gap-1">
    {content && (
      <span data-testid={`doc-row-${doc.id}-content-status`} className={`da-status ${VARIANT_MODIFIER[content.partial ? 'cond' : 'info']} shrink-0`}
        title={content.tooltip} aria-label={`${content.label}. ${content.tooltip}`}>
        <span className="da-status__dot" aria-hidden />{content.label}
      </span>
    )}
    {hasDeferredEnrichment && (
      <span data-testid={`doc-row-${doc.id}-enrichment-status`} className={`da-status ${VARIANT_MODIFIER.cond} shrink-0`}
        title={deferredTooltip} aria-label={`ENRICHMENT DEFERRED. ${deferredTooltip}`}>
        <span className="da-status__dot" aria-hidden />ENRICHMENT DEFERRED
      </span>
    )}
    {fieldChip}
  </span>;
};
